import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Disclaimer, Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { ResourceTable } from "@/components/ResourceTable";
import { PayoffCompare } from "@/components/PayoffCompare";
import { useWsData } from "@/hooks/useWsData";
import { api, brl } from "@/lib/api";

const KINDS = [["emprestimo", "Empréstimo"], ["financiamento", "Financiamento"], ["cartao", "Cartão (rotativo)"], ["cheque_especial", "Cheque especial"], ["outro", "Outro"]];
const FIELDS = [
  { name: "name", label: "Descrição", wide: true },
  { name: "kind", label: "Tipo", type: "select", default: "emprestimo", options: KINDS.map(([value, label]) => ({ value, label })) },
  { name: "balance", label: "Saldo devedor (R$)", type: "number" },
  { name: "rate_month", label: "Juros (% ao mês)", type: "number" },
  { name: "min_payment", label: "Valor da parcela (R$)", type: "number" },
  { name: "due_day", label: "Dia de vencimento", type: "number", default: 10 },
  { name: "remaining_installments", label: "Parcelas restantes", type: "number", default: 0, required: false },
  { name: "payoff_amount", label: "Valor para quitação hoje (R$)", type: "number", default: 0, required: false },
];

function Strategy({ debts }) {
  const minSum = debts.reduce((a, d) => a + d.min_payment, 0);
  const [budget, setBudget] = useState(Math.ceil(minSum * 1.2));
  const [res, setRes] = useState(null);
  useEffect(() => { setBudget(Math.ceil(minSum * 1.2)); }, [minSum]);
  const run = async () => {
    const { data } = await api.post("/simulate/debts", { debts: debts.map(({ name, balance, rate_month, min_payment }) => ({ name, balance, rate_month, min_payment })), monthly_budget: Number(budget) });
    setRes(data);
  };
  const Col = ({ r, title, desc }) => (
    <div className="rounded-xl border border-[#1E3A6E] bg-[#0B1A33] p-4" data-testid={`strategy-${r.method}`}>
      <div className="font-semibold text-[#F5D77F]">{title}</div><p className="text-xs text-slate-400">{desc}</p>
      {r.feasible ? (
        <div className="mt-3 space-y-1 text-sm"><div>Quitação em <b className="num">{r.months}</b> meses</div><div>Juros totais <b className="num">{brl(r.total_interest)}</b></div>
          <div className="text-xs text-slate-400">Ordem: {r.order.join(" → ")}</div></div>
      ) : <p className="mt-3 text-sm text-red-300">Orçamento insuficiente para cobrir as parcelas mínimas.</p>}
    </div>
  );
  return (
    <Section title="Estratégia de quitação" testid="debt-strategy">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-slate-400">Valor mensal disponível para dívidas (R$)</label>
          <Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 w-56 bg-[#0B1A33]" data-testid="debt-budget-input" /></div>
        <Button onClick={run} disabled={!debts.length} data-testid="debt-simulate-btn">Comparar estratégias</Button>
        <span className="text-xs text-slate-500">Mínimo: {brl(minSum)}</span>
      </div>
      {res && (
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Col r={res.avalanche} title="Avalanche" desc="Prioriza a dívida com maior juros — menor custo total." />
          <Col r={res.snowball} title="Bola de neve" desc="Prioriza o menor saldo — vitórias rápidas e motivação." />
        </div>
      )}
    </Section>
  );
}

export default function Dividas() {
  const { data: rows, reload, wid } = useWsData("/debts");
  if (!rows) return <Spinner />;
  const total = rows.reduce((a, d) => a + d.balance, 0);
  const interest = rows.reduce((a, d) => a + (d.balance * d.rate_month) / 100, 0);
  return (
    <div data-testid="dividas-page">
      <PageHeader eyebrow="Redução de riscos" title="Gestão de dívidas" subtitle="Organize passivos, compare estratégias e reduza o custo dos juros." />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Total devido" value={brl(total)} tone="red" testid="debts-total" />
        <Kpi label="Juros estimados/mês" value={brl(interest)} tone="gold" testid="debts-interest" />
        <Kpi label="Parcelas mínimas/mês" value={brl(rows.reduce((a, d) => a + d.min_payment, 0))} testid="debts-min" />
      </div>
      <Section className="mb-6">
        <ResourceTable wid={wid} res="debts" title="Dívidas e passivos" addLabel="Nova dívida" rows={rows} onChanged={reload} fields={FIELDS}
          columns={[{ key: "name", label: "Dívida" }, { key: "kind", label: "Tipo", render: (r) => Object.fromEntries(KINDS)[r.kind] },
            { key: "balance", label: "Saldo", right: true, render: (r) => brl(r.balance) }, { key: "rate_month", label: "Juros a.m.", right: true, render: (r) => `${Number(r.rate_month).toLocaleString("pt-BR")}%` },
            { key: "min_payment", label: "Parcela", right: true, render: (r) => brl(r.min_payment) },
            { key: "remaining_installments", label: "Restantes", right: true, render: (r) => r.remaining_installments || "—" },
            { key: "payoff_amount", label: "Quitação hoje", right: true, render: (r) => (r.payoff_amount ? brl(r.payoff_amount) : "—") }]} />
      </Section>
      <PayoffCompare debts={rows} />
      <Strategy debts={rows} />
      <Disclaimer />
    </div>
  );
}
